import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useTheme } from '../../context/ThemeContext';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiRefreshCw, FiImage } from 'react-icons/fi';
import WarningModal from './WarningModal';
import ConfirmModal from '../common/ConfirmModal';

const PromotionsManager = () => {
  const { isDarkMode } = useTheme();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

  const initialFormState = {
    title: 'Entre na lista de espera',
    subtitle: '',
    highlightText: 'NOVIDADE',
    description: 'Garanta 40% de desconto no lançamento.',
    ctaText: 'Quero Participar',
    destinationLink: '',
    whatsappNumber: '5582996992182',
    whatsappMessage: 'Olá, quero aproveitar a promoção de lançamento.',
    isActive: false,
    animationTime: 3,
    bannerColor: '#4f46e5',
    backgroundImage: '',
    textColor: '#ffffff',
    fontFamily: 'Inter'
  };

  const [formData, setFormData] = useState(initialFormState);

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'promotions'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPromotions(data);
    } catch (error) {
      console.error('Erro ao buscar promoções:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, 'promotions', editingId), {
          ...formData,
          animationTime: Number(formData.animationTime),
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'promotions'), {
          ...formData,
          animationTime: Number(formData.animationTime),
          createdAt: serverTimestamp()
        });
      }
      setIsModalOpen(false);
      setEditingId(null);
      setFormData(initialFormState);
      fetchPromotions();
    } catch (error) {
      console.error('Erro ao salvar promoção:', error);
      setWarningMessage('Ocorreu um erro ao salvar a promoção. Verifique o limite de tamanho da imagem (1MB) e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (promo) => {
    setFormData({
      title: promo.title || '',
      subtitle: promo.subtitle || '',
      highlightText: promo.highlightText || '',
      description: promo.description || '',
      ctaText: promo.ctaText || '',
      destinationLink: promo.destinationLink || '',
      whatsappNumber: promo.whatsappNumber || '',
      whatsappMessage: promo.whatsappMessage || '',
      isActive: promo.isActive || false,
      animationTime: promo.animationTime || 3,
      bannerColor: promo.bannerColor || '#4f46e5',
      backgroundImage: promo.backgroundImage || '',
      textColor: promo.textColor || '#ffffff',
      fontFamily: promo.fontFamily || 'Inter'
    });
    setEditingId(promo.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'promotions', id));
      fetchPromotions();
    } catch (error) {
      console.error('Erro ao deletar:', error);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await updateDoc(doc(db, 'promotions', id), {
        isActive: !currentStatus,
        updatedAt: serverTimestamp()
      });
      fetchPromotions();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Verificar tamanho (limite do Firestore é 1MB, vamos alertar se for muito grande, ex: > 800kb)
    if (file.size > 800 * 1024) {
      setWarningMessage('A imagem escolhida é muito grande. Por favor, selecione um arquivo menor que 800KB para garantir o salvamento correto no banco de dados.');
      return;
    }

    setUploadingImage(true);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, backgroundImage: reader.result }));
      setUploadingImage(false);
    };
    reader.onerror = () => {
      console.error('Erro ao converter imagem');
      setWarningMessage('Erro ao tentar processar e converter a imagem.');
      setUploadingImage(false);
    };
    
    reader.readAsDataURL(file);
  };

  return (
    <div className={`p-6 rounded-2xl border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Gerenciador de Promoções</h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Crie e ative campanhas promocionais dinâmicas.</p>
        </div>
        <button
          onClick={() => { setFormData(initialFormState); setEditingId(null); setIsModalOpen(true); }}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm font-semibold"
        >
          <FiPlus /> Nova Campanha
        </button>
      </div>

      {loading && promotions.length === 0 ? (
        <div className="flex justify-center py-10">
          <FiRefreshCw className="animate-spin text-indigo-500 w-8 h-8" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className={`w-full text-left border-collapse ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <thead>
              <tr className={`border-b ${isDarkMode ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-500'} text-xs uppercase tracking-wider`}>
                <th className="p-4 font-semibold">Campanha</th>
                <th className="p-4 font-semibold">Status</th>
                <th className="p-4 font-semibold">CTA / WhatsApp</th>
                <th className="p-4 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {promotions.map((promo) => (
                <tr key={promo.id} className={`border-b ${isDarkMode ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'} transition-colors`}>
                  <td className="p-4">
                    <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{promo.title}</p>
                    <p className="text-xs truncate max-w-xs">{promo.description}</p>
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => toggleStatus(promo.id, promo.isActive)}
                      className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 w-max ${
                        promo.isActive 
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}
                    >
                      {promo.isActive ? <FiCheck /> : <FiX />}
                      {promo.isActive ? 'Ativa' : 'Inativa'}
                    </button>
                  </td>
                  <td className="p-4 text-sm">
                    <p><strong>Btn:</strong> {promo.ctaText}</p>
                    <p className="text-xs opacity-70">WPP: {promo.whatsappNumber}</p>
                  </td>
                  <td className="p-4 flex items-center justify-end gap-2">
                    <button onClick={() => openEditModal(promo)} className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                      <FiEdit2 />
                    </button>
                    <button onClick={() => setDeleteConfirm({ isOpen: true, id: promo.id })} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
              {promotions.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center p-8 text-gray-500">Nenhuma campanha encontrada.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className={`w-full max-w-2xl my-8 rounded-2xl shadow-2xl relative flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`}>
            <div className={`p-6 border-b flex justify-between items-center shrink-0 ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {editingId ? 'Editar Campanha' : 'Nova Campanha'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-red-500 transition-colors">
                <FiX size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
              <form id="promoForm" onSubmit={handleSubmit} className="space-y-6">
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Título Principal</label>
                    <input required type="text" name="title" value={formData.title} onChange={handleInputChange} className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80`} placeholder="Ex: Entre na lista de espera" />
                  </div>
                  <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Badge / Destaque</label>
                    <input type="text" name="highlightText" value={formData.highlightText} onChange={handleInputChange} className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80`} placeholder="Ex: NOVIDADE" />
                  </div>
                </div>

                <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Subtítulo (Opcional)</label>
                  <input type="text" name="subtitle" value={formData.subtitle} onChange={handleInputChange} className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80`} placeholder="Texto complementar" />
                </div>

                <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Descrição</label>
                  <textarea required name="description" value={formData.description} onChange={handleInputChange} rows="2" className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80 resize-none`} placeholder="Garanta 40% de desconto no lançamento."></textarea>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Texto do Botão (CTA)</label>
                    <input required type="text" name="ctaText" value={formData.ctaText} onChange={handleInputChange} className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80`} placeholder="Quero Participar" />
                  </div>
                </div>

                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-purple-50/50 border-purple-100'} space-y-4`}>
                  <h4 className={`text-sm font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-700'} flex items-center gap-2`}><FiEdit2 /> Estilo e Tipografia</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Fonte Principal</label>
                      <select name="fontFamily" value={formData.fontFamily} onChange={handleInputChange} className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-purple-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80`}>
                        <option value="Inter">Inter</option>
                        <option value="Roboto">Roboto</option>
                        <option value="Outfit">Outfit</option>
                        <option value="Montserrat">Montserrat</option>
                        <option value="Poppins">Poppins</option>
                      </select>
                    </div>
                    <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Cor do Banner</label>
                      <div className={`flex items-center gap-3 p-1.5 rounded-xl transition-all border border-transparent focus-within:ring-2 focus-within:ring-purple-500 ${isDarkMode ? 'bg-gray-800/50 focus-within:bg-gray-800' : 'bg-gray-50 focus-within:bg-white'}`}>
  <div className="relative w-10 h-10 shrink-0 rounded-lg overflow-hidden border border-black/10 dark:border-white/10 shadow-sm">
    <input type="color" name="bannerColor" value={formData.bannerColor} onChange={handleInputChange} className="absolute -top-4 -left-4 w-20 h-20 cursor-pointer border-0 p-0" />
  </div>
  <input type="text" name="bannerColor" value={formData.bannerColor} onChange={handleInputChange} className="flex-1 bg-transparent border-0 focus:ring-0 outline-none text-sm font-medium p-0 text-gray-900 dark:text-white" />
</div>
                    </div>
                    <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Cor do Texto</label>
                      <div className={`flex items-center gap-3 p-1.5 rounded-xl transition-all border border-transparent focus-within:ring-2 focus-within:ring-purple-500 ${isDarkMode ? 'bg-gray-800/50 focus-within:bg-gray-800' : 'bg-gray-50 focus-within:bg-white'}`}>
  <div className="relative w-10 h-10 shrink-0 rounded-lg overflow-hidden border border-black/10 dark:border-white/10 shadow-sm">
    <input type="color" name="textColor" value={formData.textColor} onChange={handleInputChange} className="absolute -top-4 -left-4 w-20 h-20 cursor-pointer border-0 p-0" />
  </div>
  <input type="text" name="textColor" value={formData.textColor} onChange={handleInputChange} className="flex-1 bg-transparent border-0 focus:ring-0 outline-none text-sm font-medium p-0 text-gray-900 dark:text-white" />
</div>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-blue-50/50 border-blue-100'} space-y-4`}>
                  <h4 className={`text-sm font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'} flex items-center gap-2`}><FiImage /> Mídia e Animação</h4>
                  
                  <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Imagem de Fundo (URL ou Upload)</label>
                    <div className="flex gap-2 mb-2">
                      <input type="url" name="backgroundImage" value={formData.backgroundImage} onChange={handleInputChange} className={`flex-1 p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80`} placeholder="https://..." />
                      <label className="flex items-center justify-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl cursor-pointer transition-colors shrink-0">
                        {uploadingImage ? <FiRefreshCw className="animate-spin" /> : 'Fazer Upload'}
                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tempo de Animação do Modal (Segundos)</label>
                    <input type="number" min="1" max="10" name="animationTime" value={formData.animationTime} onChange={handleInputChange} className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80`} />
                  </div>
                </div>

                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-green-900/10 border-green-900/30' : 'bg-green-50 border-green-100'} space-y-4`}>
                  <h4 className={`text-sm font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Configuração do WhatsApp</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Número (Ex: 5511999999999)</label>
                      <input required type="text" name="whatsappNumber" value={formData.whatsappNumber} onChange={handleInputChange} className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-green-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80`} />
                    </div>
                    <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Link Direto (Opcional se WPP preenchido)</label>
                      <input type="url" name="destinationLink" value={formData.destinationLink} onChange={handleInputChange} className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80`} placeholder="Pular WhatsApp e ir pro link" />
                    </div>
                  </div>
                  
                  <div className="flex flex-col justify-end">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Mensagem Automática</label>
                    <textarea required name="whatsappMessage" value={formData.whatsappMessage} onChange={handleInputChange} rows="2" className={`w-full p-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:ring-2 focus:ring-green-500 outline-none transition-all hover:bg-gray-100 dark:hover:bg-gray-800/80 resize-none`}></textarea>
                  </div>
                </div>

                <div className={`flex items-center justify-between p-4 mt-8 rounded-xl border ${isDarkMode ? 'bg-indigo-900/10 border-indigo-500/20' : 'bg-indigo-50/50 border-indigo-100'}`}>
                  <div>
                    <h4 className={`text-sm font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>Status da Campanha</h4>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Se ativada, esta campanha substituirá qualquer outra promoção em andamento.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.isActive}
                    onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                    className={`${
                      formData.isActive ? 'bg-indigo-600' : isDarkMode ? 'bg-gray-700' : 'bg-gray-300'
                    } relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${isDarkMode ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`${
                        formData.isActive ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md ring-0 transition duration-300 ease-in-out`}
                    />
                  </button>
                </div>
              </form>
            </div>
            
            <div className={`p-6 border-t shrink-0 flex justify-end gap-4 ${isDarkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-100 bg-gray-50'} rounded-b-2xl`}>
              <button onClick={() => setIsModalOpen(false)} type="button" className={`px-6 py-2.5 rounded-xl font-bold transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-200'}`}>
                Cancelar
              </button>
              <button type="submit" form="promoForm" disabled={loading} className="px-8 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-lg hover:shadow-indigo-500/30 transition-all disabled:opacity-50 flex items-center gap-2">
                {loading && <FiRefreshCw className="animate-spin" />}
                Salvar Campanha
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
        onConfirm={() => {
          handleDelete(deleteConfirm.id);
          setDeleteConfirm({ isOpen: false, id: null });
        }}
        title="Excluir promoção"
        message="Tem certeza que deseja excluir esta promoção? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
      />

      {/* Custom Warning Modal */}
      <WarningModal 
        isOpen={!!warningMessage} 
        onClose={() => setWarningMessage('')} 
        title="Atenção" 
        message={warningMessage} 
      />
    </div>
  );
};

export default PromotionsManager;
